import { useState, useMemo } from 'react'
import { Search, Users, Calendar, ArrowRight, Phone } from 'lucide-react'
import { DatePicker } from '@/components/ui/DatePicker'
import { motion } from 'framer-motion'
import { format, addDays } from 'date-fns'
import { SkeletonRow } from '@/components/ui/Skeleton'
import { useBookings } from '@/lib/queries/useBookings'
import type { Booking, GuestProfile } from '@/lib/types/booking'

const today = new Date()
type Mode = 'upcoming' | 'all'

interface CustomerListProps {
  onSelectGuest: (profile: GuestProfile) => void
  selectedGuestKey: string | null
}

/** Canonical identity key for a booking's guest — phone first, email second, name fallback */
function guestKey(b: Booking): string {
  const phone = (b.guest?.phone ?? '').replace(/\D/g, '')
  if (phone.length >= 6) return `phone:${phone}`
  const email = (b.guest?.email ?? '').toLowerCase().trim()
  if (email) return `email:${email}`
  return `name:${(b.guest?.name ?? '').toLowerCase().trim() || b.documentId}`
}

function buildProfiles(bookings: Booking[]): Map<string, GuestProfile> {
  const map = new Map<string, GuestProfile>()
  // Sort newest first so first-encountered = most recent
  const sorted = [...bookings].sort((a, b) =>
    b.date === a.date ? b.time.localeCompare(a.time) : b.date.localeCompare(a.date),
  )
  for (const b of sorted) {
    const key = guestKey(b)
    const existing = map.get(key)
    if (!existing) {
      map.set(key, {
        key,
        name: b.guest?.name ?? '',
        phone: b.guest?.phone ?? '',
        email: b.guest?.email ?? '',
        bookings: [b],
        visitCount: 1,
        totalPeople: b.people,
      })
    } else {
      existing.bookings.push(b)
      existing.visitCount++
      existing.totalPeople += b.people
      // Use most recent contact details
      if (!existing.phone && b.guest?.phone) existing.phone = b.guest.phone
      if (!existing.email && b.guest?.email) existing.email = b.guest.email
    }
  }
  return map
}

export function CustomerList({ onSelectGuest, selectedGuestKey }: CustomerListProps) {
  const [mode, setMode] = useState<Mode>('upcoming')
  const [search, setSearch] = useState('')
  const [dateFrom, setDateFrom] = useState(today)
  const [dateTo, setDateTo] = useState(addDays(today, 30))
  const { data: upcomingResp, isLoading: upcomingLoading } = useBookings({
    dateFrom: format(dateFrom, 'yyyy-MM-dd'),
    dateTo: format(dateTo, 'yyyy-MM-dd'),
    pageSize: 200,
  })

  const { data: allResp, isLoading: allLoading } = useBookings({
    pageSize: 500,
  })

  // Build the profile map from ALL bookings (used in both tabs for lookup)
  const profileMap = useMemo(() => buildProfiles(allResp?.data ?? []), [allResp])

  // All Guests list: sorted alphabetically, filtered client-side (no debounce — all data is in memory)
  const allGuests = useMemo(() => {
    const profiles = Array.from(profileMap.values()).sort((a, b) => a.name.localeCompare(b.name))
    if (!search) return profiles
    const q = search.toLowerCase()
    return profiles.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.email.toLowerCase().includes(q) ||
        p.phone.includes(q),
    )
  }, [profileMap, search])

  // Upcoming bookings: also filtered client-side
  const upcomingBookings = useMemo(() => {
    const all = upcomingResp?.data ?? []
    if (!search) return all
    const q = search.toLowerCase()
    return all.filter(
      (b) =>
        (b.guest?.name ?? '').toLowerCase().includes(q) ||
        (b.guest?.email ?? '').toLowerCase().includes(q) ||
        (b.guest?.phone ?? '').includes(q),
    )
  }, [upcomingResp, search])

  const isLoading = mode === 'upcoming' ? upcomingLoading : allLoading

  const handleUpcomingClick = (b: Booking) => {
    const key = guestKey(b)
    // Use the full profile if available; fall back to single-booking profile
    const profile = profileMap.get(key) ?? {
      key,
      name: b.guest?.name ?? '',
      phone: b.guest?.phone ?? '',
      email: b.guest?.email ?? '',
      bookings: [b],
      visitCount: 1,
      totalPeople: b.people,
    }
    onSelectGuest(profile)
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Mode tabs */}
      <div className="flex border-b border-[var(--color-border-subtle)]">
        {(['upcoming', 'all'] as Mode[]).map((m) => (
          <button
            key={m}
            onClick={() => { setMode(m); setSearch('') }}
            className={`flex-1 py-2.5 text-xs font-medium transition-colors cursor-pointer ${
              mode === m
                ? 'text-[var(--color-accent-amber)] border-b-2 border-[var(--color-accent-amber)]'
                : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]'
            }`}
          >
            {m === 'upcoming' ? 'Upcoming' : 'All Guests'}
          </button>
        ))}
      </div>

      {/* Date range (upcoming mode only) */}
      {mode === 'upcoming' && (
        <div className="flex items-center gap-1.5 px-3 py-2 border-b border-[var(--color-border-subtle)]">
          <div className="flex-1 min-w-0">
            <DatePicker
              value={format(dateFrom, 'yyyy-MM-dd')}
              onChange={(v) => {
                const d = new Date(v + 'T12:00:00')
                setDateFrom(d)
                if (d > dateTo) setDateTo(d)
              }}
            />
          </div>
          <ArrowRight className="h-3 w-3 text-[var(--color-text-muted)] shrink-0" />
          <div className="flex-1 min-w-0">
            <DatePicker
              value={format(dateTo, 'yyyy-MM-dd')}
              onChange={(v) => {
                const d = new Date(v + 'T12:00:00')
                setDateTo(d)
                if (d < dateFrom) setDateFrom(d)
              }}
            />
          </div>
        </div>
      )}

      {/* Search */}
      <div className="p-3 border-b border-[var(--color-border-subtle)]">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--color-text-muted)]" />
          <input
            placeholder={mode === 'upcoming' ? 'Search guests...' : 'Search all guests...'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-9 pl-9 pr-3 rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] border border-[var(--color-border-default)] focus:outline-none focus:border-[var(--color-accent-amber)]/50"
          />
        </div>
      </div>

      {/* List */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {isLoading ? (
          <div className="space-y-1 p-2">
            {Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} />)}
          </div>
        ) : mode === 'upcoming' ? (
          upcomingBookings.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Calendar className="h-10 w-10 text-[var(--color-text-muted)] mb-3" />
              <p className="text-sm text-[var(--color-text-muted)]">No bookings in this range</p>
            </div>
          ) : (
            <div className="p-2 space-y-1">
              {upcomingBookings.map((b) => {
                const key = guestKey(b)
                const profile = profileMap.get(key)
                const visits = profile?.visitCount ?? 1
                return (
                  <GuestRow
                    key={b.documentId}
                    name={b.guest?.name ?? 'Unknown'}
                    subtitle={`${b.date} · ${b.time} · ${b.people} guests`}
                    phone={b.guest?.phone}
                    visits={visits}
                    isSelected={selectedGuestKey === key}
                    onClick={() => handleUpcomingClick(b)}
                  />
                )
              })}
            </div>
          )
        ) : allGuests.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Users className="h-10 w-10 text-[var(--color-text-muted)] mb-3" />
            <p className="text-sm text-[var(--color-text-muted)]">No guests found</p>
          </div>
        ) : (
          <div className="p-2 space-y-1">
            {allGuests.map((profile) => (
              <GuestRow
                key={profile.key}
                name={profile.name || 'Unknown'}
                subtitle={profile.phone || profile.email || 'No contact info'}
                phone={profile.phone}
                visits={profile.visitCount}
                isSelected={selectedGuestKey === profile.key}
                onClick={() => onSelectGuest(profile)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function GuestRow({
  name, subtitle, phone, visits, isSelected, onClick,
}: {
  name: string
  subtitle: string
  phone?: string
  visits: number
  isSelected: boolean
  onClick: () => void
}) {
  return (
    <motion.button
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.15 }}
      onClick={onClick}
      className={`flex items-center gap-3 w-full rounded-[var(--radius-md)] p-3 text-left transition-colors cursor-pointer ${
        isSelected
          ? 'bg-[var(--color-accent-amber-dim)] border border-[var(--color-accent-amber)]/20'
          : 'hover:bg-[var(--color-bg-elevated)]'
      }`}
    >
      <div className="h-9 w-9 rounded-full bg-[var(--color-bg-elevated)] flex items-center justify-center text-sm font-semibold text-[var(--color-text-primary)] shrink-0">
        {name.charAt(0).toUpperCase()}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-[var(--color-text-primary)] truncate">{name}</p>
        <div className="flex items-center gap-1.5 text-xs text-[var(--color-text-muted)] truncate">
          {phone && <Phone className="h-3 w-3 shrink-0" />}
          <span className="truncate">{subtitle}</span>
        </div>
      </div>
      {visits > 1 && (
        <span className="shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)]">
          {visits}×
        </span>
      )}
    </motion.button>
  )
}
